import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { SelaProjectDetail } from '../../../services/selaApi';
import { recordV2FunnelStep } from '../../../utils/v2Rum';
import { AskSelaDrawer } from './AskSelaDrawer';

export type AskSelaSourcePage =
  | 'overview'
  | 'insights'
  | 'roadmap'
  | 'people'
  | 'documents'
  | 'graph'
  | 'changelog';

export type AskSelaContextType =
  | 'project'
  | 'decisionBrief'
  | 'recommendation'
  | 'developmentPath'
  | 'risk'
  | 'missingFact'
  | 'nextAction'
  | 'requirement'
  | 'person'
  | 'roleNeed'
  | 'followUp'
  | 'document'
  | 'graphNode'
  | 'evidence'
  | 'changelogEntry';

export type AskSelaFocus = {
  type: AskSelaContextType;
  label: string;
  sourceId?: string;
  sourcePage?: AskSelaSourcePage;
  summary?: string;
  metadata?: Record<string, unknown>;
};

type AskSelaContextValue = {
  isOpen: boolean;
  focus: AskSelaFocus;
  openAskSela: (focus?: AskSelaFocus, draft?: string) => void;
  closeAskSela: () => void;
  clearAskSelaFocus: () => void;
  pendingDraft: string;
  consumePendingDraft: () => string;
};

const AskSelaReactContext = createContext<AskSelaContextValue | null>(null);

function projectFocus(project: SelaProjectDetail): AskSelaFocus {
  return {
    type: 'project',
    label: 'Project-wide',
    sourcePage: 'overview',
    summary: [
      project.title,
      project.city ? `${project.city}${project.state ? `, ${project.state.toUpperCase()}` : ''}` : '',
      project.parcelPin ? `Parcel ${project.parcelPin}` : '',
    ].filter(Boolean).join(' | '),
  };
}

export function AskSelaProvider({
  project,
  intelKickoffInProgress,
  children,
}: {
  project: SelaProjectDetail;
  intelKickoffInProgress?: boolean;
  children: React.ReactNode;
}) {
  const defaultFocus = useMemo(() => projectFocus(project), [project]);
  const [isOpen, setIsOpen] = useState(false);
  const [focus, setFocus] = useState<AskSelaFocus>(defaultFocus);
  const [pendingDraft, setPendingDraft] = useState('');

  const openAskSela = useCallback(
    (nextFocus?: AskSelaFocus, draft = '') => {
      const resolvedFocus = nextFocus || defaultFocus;
      setFocus(resolvedFocus);
      setPendingDraft(draft);
      setIsOpen(true);
      recordV2FunnelStep('ask_sela_open', 'click', {
        source_page: resolvedFocus.sourcePage || 'overview',
        focus_type: resolvedFocus.type,
        has_draft: Boolean(draft.trim()),
      }, project.id);
    },
    [defaultFocus, project.id],
  );

  const clearAskSelaFocus = useCallback(() => {
    setFocus(defaultFocus);
  }, [defaultFocus]);

  const closeAskSela = useCallback(() => setIsOpen(false), []);

  const consumePendingDraft = useCallback(() => {
    const value = pendingDraft;
    setPendingDraft('');
    return value;
  }, [pendingDraft]);

  const value = useMemo(
    () => ({
      isOpen,
      focus,
      openAskSela,
      closeAskSela,
      clearAskSelaFocus,
      pendingDraft,
      consumePendingDraft,
    }),
    [clearAskSelaFocus, closeAskSela, consumePendingDraft, focus, isOpen, openAskSela, pendingDraft],
  );

  return (
    <AskSelaReactContext.Provider value={value}>
      {children}
      <AskSelaDrawer project={project} intelKickoffInProgress={intelKickoffInProgress} />
    </AskSelaReactContext.Provider>
  );
}

export function useAskSela() {
  const value = useContext(AskSelaReactContext);
  if (!value) {
    throw new Error('useAskSela must be used within AskSelaProvider');
  }
  return value;
}
